import React from "react";
import { useTranslation } from "react-i18next";

interface FileUploadWidgetProps {
  label: string;
  fileUrl?: string;
  onUpload: (file: File) => void;
  isUploading: boolean;
  accept?: string;
  viewLabel?: string;
  uploadLabel?: string;
  replaceLabel?: string;
}

/**
 * Reusable file upload widget with view/upload/replace functionality.
 * Used for CV and Blind CV uploads in TechCheckForm.
 */
export const FileUploadWidget: React.FC<FileUploadWidgetProps> = ({
  label,
  fileUrl,
  onUpload,
  isUploading,
  accept = ".pdf,.doc,.docx",
  viewLabel,
  uploadLabel,
  replaceLabel,
}) => {
  const { t } = useTranslation(["common"]);
  const resolvedViewLabel = viewLabel || t("fileUpload.viewFile");
  const resolvedUploadLabel = uploadLabel || t("fileUpload.upload");
  const resolvedReplaceLabel = replaceLabel || t("fileUpload.replace");
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <div className="tech-check-field">
      <span className="info-label">{label}:</span>
      <div className="file-upload-section">
        {fileUrl ? (
          <div className="file-link">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              {resolvedViewLabel}
            </a>
            <span className="file-actions">
              <label className="file-upload-label">
                {resolvedReplaceLabel}
                <input
                  type="file"
                  accept={accept}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  disabled={isUploading}
                />
              </label>
            </span>
          </div>
        ) : (
          <label className="file-upload-label">
            {isUploading ? t("fileUpload.uploading") : resolvedUploadLabel}
            <input
              type="file"
              accept={accept}
              onChange={handleFileChange}
              style={{ display: "none" }}
              disabled={isUploading}
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default FileUploadWidget;
